import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';

import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';

import { getSessionIdToken } from '../auth/apiAuth';
import { SERVICE_COLORS } from '../utility/theme';
import Picker from '../utility/Pickers';

import axios from 'axios';

const statusColor = (status) => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (s.includes('completed')) return 'success';
    if (s.includes('error') || s.includes('failed')) return 'error';
    if (s.includes('processing') || s.includes('running')) return 'warning';
    return 'default';
};

const statusLabel = (status) => {
    if (!status) return '-';
    const s = status.toLowerCase();
    if (s.includes('completed')) return 'Completed';
    if (s.includes('error') || s.includes('failed')) return 'Error';
    if (s.includes('processing') || s.includes('running')) return 'Processing';
    return status;
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        '  ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const formatBytes = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function LabelsHistory({ auth, selectedProject, templates, onSnackbar }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nextToken, setNextToken] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [pdfUrls, setPdfUrls] = useState({});
    const [filterStatus, setFilterStatus] = useState('');
    const [filterUsers, setFilterUsers] = useState([]);
    const [filterDateStart, setFilterDateStart] = useState(null);
    const [filterDateEnd, setFilterDateEnd] = useState(null);

    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const labelsColor = SERVICE_COLORS.labels.main;

    const retentionDays = (templates || []).find(t => t.project_id === selectedProject)?.retention_days;

    const isExpired = (createdAt) => {
        if (!retentionDays || !createdAt) return false;
        return Date.now() - new Date(createdAt).getTime() > retentionDays * 24 * 60 * 60 * 1000;
    };

    const fetchRenders = useCallback(async (token_cursor = null) => {
        setLoading(true);
        setError(null);
        try {
            const token = await getSessionIdToken();
            const params = { limit: 50 };
            if (selectedProject) params.project_id = selectedProject;
            if (token_cursor) params.next_token = token_cursor;

            const { data } = await axios.get(`${API_BASE}/labels/renders`, {
                headers: { Authorization: token },
                params
            });

            const newRows = (data.renders || []).map((r, idx) => ({
                ...r,
                id: r.render_id || `${r.created_at}-${idx}`,
            }));

            if (token_cursor) {
                setRows(prev => [...prev, ...newRows]);
            } else {
                setRows(newRows);
            }

            setNextToken(data.next_token || null);
            setHasMore(data.has_more || false);
        } catch (err) {
            setError('Error loading render history');
        } finally {
            setLoading(false);
        }
    }, [selectedProject, API_BASE]);

    useEffect(() => {
        fetchRenders();
    }, [fetchRenders]);

    const handleOpenPdf = async (renderId) => {
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.get(`${API_BASE}/labels/renders/${renderId}`, {
                headers: { Authorization: token }
            });
            if (data.pdf_url) {
                window.open(data.pdf_url, '_blank');
            }
        } catch (err) {
            onSnackbar('Error opening the PDF', 'error');
        }
    };

    const handleDownloadPdf = async (renderId) => {
        try {
            const token = await getSessionIdToken();
            const { data } = await axios.get(`${API_BASE}/labels/renders/${renderId}`, {
                headers: { Authorization: token }
            });
            if (data.pdf_url) {
                const res = await fetch(data.pdf_url);
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `labels-${renderId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            onSnackbar('Error downloading the PDF', 'error');
        }
    };


    const handleRowClick = async (renderId) => {
        if (expandedRow === renderId) {
            setExpandedRow(null);
            return;
        }
        setExpandedRow(renderId);
        if (!pdfUrls[renderId]) {
            try {
                const token = await getSessionIdToken();
                const { data } = await axios.get(`${API_BASE}/labels/renders/${renderId}`, {
                    headers: { Authorization: token }
                });
                if (data.pdf_url) {
                    setPdfUrls(prev => ({ ...prev, [renderId]: data.pdf_url }));
                }
            } catch (err) { /* ignore */ }
        }
    };

    const statuses = [...new Set(rows.map(r => r.status).filter(Boolean))];
    const users = [...new Set(rows.map(r => r.requested_by).filter(Boolean))];

    const filteredRows = rows.filter(r => {
        if (filterStatus && r.status !== filterStatus) return false;
        if (filterUsers.length > 0 && !filterUsers.includes(r.requested_by)) return false;
        if (filterDateStart && r.created_at) {
            if (new Date(r.created_at).getTime() < filterDateStart) return false;
        }
        if (filterDateEnd && r.created_at) {
            if (new Date(r.created_at).getTime() > filterDateEnd) return false;
        }
        return true;
    });

    return (
        <Box sx={{ p: 3, mx: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" color={labelsColor} fontWeight={600}>History</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Picker
                        nome="start-date"
                        dataStato={filterDateStart || Date.now() - 30 * 24 * 60 * 60 * 1000}
                        handleDateChange={(date) => setFilterDateStart(new Date(date).getTime())}
                    />
                    <Picker
                        nome="end-date"
                        dataStato={filterDateEnd || Date.now()}
                        handleDateChange={(date) => {
                            const d = new Date(date);
                            setFilterDateEnd(new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime());
                        }}
                    />
                    <FormControl sx={{ minWidth: 140 }}>
                        <Select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            displayEmpty
                            variant="outlined"
                        >
                            <MenuItem value="">All statuses</MenuItem>
                            {statuses.map(s => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 160 }}>
                        <Select
                            multiple
                            value={filterUsers}
                            onChange={(e) => setFilterUsers(e.target.value)}
                            displayEmpty
                            variant="outlined"
                            renderValue={(sel) => sel.length === 0 ? 'All users' : sel.join(', ')}
                        >
                            {users.map(u => (
                                <MenuItem key={u} value={u}>
                                    <Checkbox size="small" checked={filterUsers.includes(u)} />
                                    {u}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton onClick={() => fetchRenders()} disabled={loading} size="small">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {retentionDays && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    PDF files are automatically deleted after <strong>{retentionDays} days</strong>.
                </Alert>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && rows.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <TableRow
                                            hover
                                            sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRow === row.id ? 'none' : undefined } }}
                                            onClick={() => handleRowClick(row.id)}
                                            selected={expandedRow === row.id}
                                        >
                                            <TableCell sx={{ width: 40, pl: 1 }}>
                                                <IconButton size="small">
                                                    {expandedRow === row.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(row.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={statusLabel(row.status)}
                                                    size="small"
                                                    color={statusColor(row.status)}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {row.requested_by || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.source === 'webapp' ? (
                                                    <Chip label="WebApp" size="small" color="primary" variant="outlined" />
                                                ) : row.source === 'api' ? (
                                                    <Chip label="API" size="small" color="warning" variant="outlined" />
                                                ) : (row.source || '-')}
                                            </TableCell>
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                {row.status === 'completed' && (
                                                    <Tooltip title={isExpired(row.created_at) ? 'PDF expired' : 'Open PDF'}>
                                                        <span>
                                                            <IconButton size="small" onClick={() => handleOpenPdf(row.render_id)} disabled={isExpired(row.created_at)}>
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0, px: 0, border: 'none' }}>
                                                <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                        <Stack direction="row" spacing={3}>
                                                            <Box sx={{ width: 120, height: 160, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'white', flexShrink: 0, position: 'relative' }}>
                                                                {pdfUrls[row.id] ? (
                                                                    <Box
                                                                        component="iframe"
                                                                        src={`${pdfUrls[row.id]}#page=1&toolbar=0&navpanes=0&scrollbar=0`}
                                                                        title="PDF preview"
                                                                        scrolling="no"
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            width: 600,
                                                                            height: 800,
                                                                            border: 'none',
                                                                            transform: 'scale(0.2)',
                                                                            transformOrigin: 'top left',
                                                                            pointerEvents: 'none',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        {row.status === 'completed' ? (
                                                                            <CircularProgress size={20} />
                                                                        ) : (
                                                                            <AutoAwesomeMosaicIcon sx={{ fontSize: 30, color: 'grey.400' }} />
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            <Box>
                                                                {row.render_id && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Render ID:</strong> {row.render_id}
                                                                    </Typography>
                                                                )}
                                                                {(row.source_file || row.file_name) && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>File:</strong> {row.source_file || row.file_name}
                                                                    </Typography>
                                                                )}
                                                                {row.labels_count > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Labels generated:</strong> {row.labels_count}
                                                                    </Typography>
                                                                )}
                                                                {row.created_at && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Date:</strong> {formatDate(row.created_at)}
                                                                    </Typography>
                                                                )}
                                                                {row.pages > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Pages:</strong> {row.pages}
                                                                    </Typography>
                                                                )}
                                                                {row.pdf_size_bytes > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Size:</strong> {formatBytes(row.pdf_size_bytes)}
                                                                    </Typography>
                                                                )}
                                                                {row.total_time_ms > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Render time:</strong> {(row.total_time_ms / 1000).toFixed(1)}s
                                                                    </Typography>
                                                                )}
                                                                {row.requested_by && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Requested by:</strong> {row.requested_by}
                                                                    </Typography>
                                                                )}
                                                                {row.options && Object.keys(row.options).length > 0 && (
                                                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>Options:</strong> {Object.entries(row.options).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                                    </Typography>
                                                                )}
                                                                {row.errors?.length > 0 && (
                                                                    <Alert severity="error" sx={{ mt: 1 }}>
                                                                        {row.errors.map((e, i) => (
                                                                            <Typography key={i} variant="caption" display="block">{e.message || e.code}</Typography>
                                                                        ))}
                                                                    </Alert>
                                                                )}
                                                                {row.status === 'completed' && isExpired(row.created_at) && (
                                                                    <Alert severity="warning" sx={{ mt: 1.5 }} icon={false}>
                                                                        PDF expired — deleted after {retentionDays} days.
                                                                    </Alert>
                                                                )}
                                                                {row.status === 'completed' && !isExpired(row.created_at) && (
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        startIcon={<DownloadIcon />}
                                                                        onClick={() => handleDownloadPdf(row.render_id)}
                                                                        sx={{ mt: 1.5, bgcolor: labelsColor, '&:hover': { bgcolor: SERVICE_COLORS.labels.dark } }}
                                                                    >
                                                                        Download PDF
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {filteredRows.length === 0 && !loading && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">No renders found.</Typography>
                        </Box>
                    )}

                    {hasMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => fetchRenders(nextToken)}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={16} /> : null}
                            >
                                Load more
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}
